import { Controller } from '@hotwired/stimulus';

// Candidature documents are stored encrypted to the managing organization's public key, so
// the server can only hand over the ciphertext envelope. Decryption needs the org private
// key, which exists only inside an unlocked vault in this browser.
//
// window.vault is the vault stimulus controller; decryptMessage(envelope, false) returns an
// ArrayBuffer rather than a string, which is what a binary file needs.
export default class extends Controller {
  static values = { url: String, filename: String, contentType: String };

  async download(event) {
    event.preventDefault();

    const vault = window.vault;
    if (!vault) {
      // no vault controller on the page at all: the user has no org key here
      window.showMessage?.(
        this.element.dataset.vaultMissingMessage || 'Your vault is not available.'
      );
      return;
    }

    // openModal runs the callback once the vault is unlocked, so one click is enough
    if (!vault.is_open()) {
      vault.openModal(() => this.download(event));
      return;
    }

    this.element.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: 'text/plain' } });
      if (!response.ok) throw new Error(`download failed: ${response.status}`);

      const envelope = (await response.text()).trim();
      const plaintext = await vault.decryptMessage(envelope, false);
      if (!plaintext) throw new Error('decryption returned nothing');

      this.save(plaintext);
    } catch (e) {
      console.error(`Could not decrypt document: ${e}`);
      window.showMessage?.(
        this.element.dataset.failureMessage || 'Could not decrypt this document.'
      );
    } finally {
      this.element.removeAttribute('aria-busy');
    }
  }

  save(buffer) {
    const blob = new Blob([buffer], {
      type: this.contentTypeValue || 'application/octet-stream',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.filenameValue || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // let the download start before the blob url is invalidated
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
